import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Floor as FloorModel } from '../../../models/Floor';
import { TabSummary } from '../../../models/TabSummary';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { assignFloor, getFloorById, getFloorsNamesWithPagination } from '../../../services/floorService';
import FloorCard from '../../../components/floor/FloorCard';
import Description from '../../../components/description/Description';
import LinkedToTab from '../../../components/floor/LinkedToTab';
import { toast } from 'react-toastify';


const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware/floors';
const itemPath = '/access-control/hardware/floor/';

const Floor: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.linkedTo), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const initialFloor: FloorModel = {
        id: 0,
        name: '',
        building: undefined,
        level: '',
        site: '',
        inputs: '',
        buildingId: undefined,
        description: '',
        default: 0
    }
    const [currentItem, setCurrentItem] = useState<FloorModel>(initialFloor);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();

    /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [
        {
            content: <LinkedToTab floorId={itemId}/>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];

    const topic: Topic = {
        accessor: 'floorName',
        title: translate.byKey(TranslationKeys.floors),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    /**
     * Fetch all the required data on a re-render
     */
    const reloadPage = async () => {
        try {
            let floor: any;
            setTabs([]);
            if (itemId) {
                floor = await getFloorById(itemId);
                setSelectedItem({ label: floor.name, value: floor.id.toString() })
                setCurrentItem(floor);
                setTabs(initialTabs);
            } else {
                setCurrentItem(initialFloor);
                setTabs([initialTabs[1]]);
            }

            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.floors) },
                { value: itemPath + `${itemId}`, displayName: `${floor?.name ?? ''}` }
            ]);

        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveFloor = (values: any) => {
        const floor: FloorModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            building: values.building,
            buildingId: values.buildingId,
            level: values.level,
            site: itemId ? values.site : site.label,
            description: values.description
        }

        const createNewFloor = itemId ? +itemId < 0 : true;
        saveItem(floor, createNewFloor);
    }

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const saveItem = (item: FloorModel, createNewItem: boolean) => {
        setBusy(true);
        assignFloor(item, createNewItem).then(async (value: any) => {
            setItemId(value.id);
            history.push(`${itemPath}${value.id}`);
            toast.success(translate.byKey(TranslationKeys.floorSaved));
            reloadPage();
        })
        .catch(failure => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(failure)
            });
        })
        .finally(() => {
            setBusy(false);
        });
    }

    return <div className='flex h-full'>
        <TopicContextProvider dataMethod={getFloorsNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveFloor}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}>
                    <FloorCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
}

export default Floor;
